<script setup>
import { ref, computed, inject, onMounted, onBeforeUnmount, watch } from "vue";
import {
  SelectTrigger as SelectTriggerPrimitive,
  SelectIcon,
  useForwardProps,
} from "reka-ui";
import { ChevronDown, X } from "lucide-vue-next";
import { cn } from "@/shared/lib/utils";
import { reactiveOmit } from "@vueuse/core";

const props = defineProps({
  class: { type: [String, Object, Array], default: undefined },
  size: { type: String, default: undefined }, // sm | default | lg; falls back to Select's size
  asChild: { type: Boolean, default: undefined },
  disabled: { type: Boolean, default: undefined },
  clearable: { type: Boolean, default: true },
  hasValue: { type: Boolean, default: undefined },
});

const injectedSize = inject("selectSize", null);
const resolvedSize = computed(
  () => props.size ?? injectedSize?.value ?? "default"
);

const emit = defineEmits(["clear"]);

const delegatedProps = reactiveOmit(
  props,
  "class",
  "size",
  "clearable",
  "hasValue"
);
const forwardedProps = useForwardProps(delegatedProps);

const triggerRef = ref(null);
const hasPlaceholder = ref(true);
let observer = null;

function checkPlaceholder() {
  const el = triggerRef.value?.$el;
  if (el) {
    hasPlaceholder.value = el.hasAttribute("data-placeholder");
  }
}

onMounted(() => {
  const el = triggerRef.value?.$el;
  if (el) {
    checkPlaceholder();
    observer = new MutationObserver(checkPlaceholder);
    observer.observe(el, {
      attributes: true,
      attributeFilter: ["data-placeholder"],
    });
  }
});

onBeforeUnmount(() => {
  observer?.disconnect();
});

// Also check when hasValue prop changes
watch(() => props.hasValue, checkPlaceholder);

function showClear() {
  if (!props.clearable) return false;
  if (props.hasValue !== undefined) return props.hasValue;
  return !hasPlaceholder.value;
}

function onClear(e) {
  e.stopPropagation();
  e.preventDefault();
  emit("clear");
}
</script>

<template>
  <SelectTriggerPrimitive
    ref="triggerRef"
    data-slot="select-trigger"
    :data-size="resolvedSize"
    v-bind="forwardedProps"
    :class="
      cn(
        'text-foreground border-input data-[placeholder]:text-muted-foreground data-[placeholder]:text-xs [&_svg:not([class*=\'text-\'])]:text-muted-foreground focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive dark:bg-input/30 dark:hover:bg-input/50 flex w-full min-w-0 items-center justify-between gap-2 rounded-md border bg-transparent px-3 py-2 text-sm transition-[color,box-shadow] outline-none focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50 data-[size=lg]:h-10 data-[size=default]:h-9 data-[size=sm]:h-8 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*=\'size-\'])]:size-4',
        props.class
      )
    "
  >
    <span class="min-w-0 truncate">
      <slot />
    </span>
    <span class="flex items-center shrink-0">
      <button
        v-if="showClear()"
        type="button"
        class="pointer-events-auto rounded-sm opacity-50 hover:opacity-100 mr-1"
        @pointerdown.stop
        @click.stop.prevent="onClear"
      >
        <X class="size-3.5" />
      </button>
      <SelectIcon as-child>
        <ChevronDown class="size-4 opacity-50" />
      </SelectIcon>
    </span>
  </SelectTriggerPrimitive>
</template>
